import { useState } from 'react';

/**
 * Простий хук валідації обов'язкових полів форми.
 *
 * Використання:
 * ```
 * const v = useFormValidation();
 * function onSave() {
 *   if (!v.check([
 *     ['organization_id', form.organization_id, 'Оберіть організацію'],
 *     ['document_number', form.document_number.trim(), "Заповніть номер документа"],
 *   ])) return;
 *   create.mutate();
 * }
 * // в input:
 * <input style={v.inputStyle('document_number')} ... />
 * {v.error('document_number')}
 * ```
 */
export function useFormValidation() {
  const [errors, setErrors] = useState<Record<string, string>>({});

  return {
    set(field: string, message: string): true {
      setErrors((e) => ({ ...e, [field]: message }));
      return true;
    },
    reset() {
      setErrors({});
    },
    clear(field: string) {
      setErrors((e) => {
        if (!e[field]) return e;
        const copy = { ...e };
        delete copy[field];
        return copy;
      });
    },
    /**
     * Перевірити список обов'язкових полів.
     * Кожний запис: [field, value, message]. Порожнє значення → помилка.
     */
    check(checks: [field: string, value: unknown, message: string][]): boolean {
      const newErrors: Record<string, string> = {};
      for (const [field, value, message] of checks) {
        const isEmpty = value == null || value === '' ||
          (typeof value === 'string' && value.trim() === '') ||
          (typeof value === 'number' && (isNaN(value) || value === 0));
        if (isEmpty) {
          newErrors[field] = message;
        }
      }
      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    hasErrors(): boolean {
      return Object.keys(errors).length > 0;
    },
    has(field: string): boolean {
      return !!errors[field];
    },
    /** JSX з повідомленням про помилку, або null. */
    error(field: string) {
      const msg = errors[field];
      if (!msg) return null;
      return (
        <div style={{
          color: 'var(--danger)',
          fontSize: '0.75rem',
          marginTop: '0.2rem',
        }}>
          {msg}
        </div>
      );
    },
    /** CSS-стиль для підсвічування input при помилці. */
    inputStyle(field: string): React.CSSProperties {
      if (!errors[field]) return {};
      return {
        borderColor: 'var(--danger)',
        boxShadow: '0 0 0 3px rgba(220, 38, 38, 0.1)',
      };
    },
    /** Загальний банер угорі форми, якщо є будь-які помилки. */
    banner() {
      const count = Object.keys(errors).length;
      if (count === 0) return null;
      return (
        <div style={{
          background: 'var(--danger-soft)',
          border: '1px solid #fecaca',
          color: 'var(--danger)',
          padding: '0.5rem 0.75rem',
          borderRadius: 'var(--radius-sm)',
          marginBottom: '1rem',
          fontSize: '0.875rem',
        }}>
          ⚠ Заповніть обов'язкові поля (позначені <strong>*</strong>):
          {' '}{Object.values(errors).join('; ')}
        </div>
      );
    },
  };
}
